import { motion, AnimatePresence } from 'framer-motion';
import { HiArrowPath, HiCheckCircle, HiClock } from 'react-icons/hi2';

// Função local para formatar última atualização
const formatLastUpdate = (date: Date | null): string => {
  if (!date) return 'Nunca';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  if (diffMinutes < 1) return 'Agora mesmo';
  if (diffMinutes === 1) return 'há 1 minuto';
  if (diffMinutes < 60) return `há ${diffMinutes} minutos`;
  if (diffHours === 1) return 'há 1 hora';
  if (diffHours < 24) return `há ${diffHours} horas`;
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

interface DataRefreshStatusProps {
  isRefreshing: boolean;
  lastUpdated: Date | null;
  refreshCount: number;
  onManualRefresh?: () => void;
  className?: string;
}

export const DataRefreshStatus: React.FC<DataRefreshStatusProps> = ({
  isRefreshing,
  lastUpdated,
  refreshCount,
  onManualRefresh,
  className = ''
}) => {
  return (
    <motion.div 
      className={`flex items-center gap-3 ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Indicador de status */}
      <div className="flex items-center gap-2">
        <AnimatePresence mode="wait">
          {isRefreshing ? (
            <motion.div
              key="refreshing"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-1"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <HiArrowPath className="w-4 h-4 text-blue-500" />
              </motion.div>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Atualizando...
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="updated"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-1"
            >
              <motion.div
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <HiCheckCircle className="w-4 h-4 text-green-500" />
              </motion.div>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Atualizado
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Separador */}
      <div className="h-3 w-px bg-gray-300 dark:bg-gray-600"></div>

      {/* Última atualização */}
      <div className="flex items-center gap-1">
        <HiClock className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatLastUpdate(lastUpdated)}
        </span>
      </div>

      {/* Contador de atualizações (apenas se > 0) */}
      {refreshCount > 0 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="flex items-center"
        >
          <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full font-medium">
            {refreshCount}
          </span>
        </motion.div>
      )}

      {/* Botão manual de refresh */}
      {onManualRefresh && (
        <motion.button
          onClick={onManualRefresh}
          disabled={isRefreshing}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`
            p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center
            ${isRefreshing 
              ? 'text-blue-500 dark:text-blue-400 cursor-not-allowed' 
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
          `}
          title="Atualizar dados"
        >
          <motion.div
            animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
            transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: "linear" } : {}}
          >
            <HiArrowPath className="w-3 h-3" />
          </motion.div>
        </motion.button>
      )}
    </motion.div>
  );
};